import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';

export default function VerifyEmail() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid verification link');
            return;
        }

        const verify = async () => {
            try {
                await apiFetch(`/auth/verify-email?token=${token}`);
                setStatus('success');
            } catch (error) {
                setStatus('error');
                setMessage(error instanceof Error ? error.message : 'Verification failed');
            }
        };

        verify();
    }, [token]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md p-8 text-center">
                {status === 'loading' && (
                    <div className="flex flex-col items-center">
                        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                        <h1 className="text-2xl font-bold mb-2">Verifying Email...</h1>
                        <p className="text-muted-foreground">Please wait while we verify your email address.</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="flex flex-col items-center">
                        <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                        <h1 className="text-2xl font-bold mb-2">Email Verified!</h1>
                        <p className="text-muted-foreground mb-6">Your email has been successfully verified.</p>
                        <Button onClick={() => navigate('/login')} className="w-full">
                            Go to Login
                        </Button>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center">
                        <XCircle className="h-12 w-12 text-destructive mb-4" />
                        <h1 className="text-2xl font-bold mb-2">Verification Failed</h1>
                        <p className="text-muted-foreground mb-6">{message}</p>
                        <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
                            Back to Login
                        </Button>
                    </div>
                )}
            </Card>
        </div>
    );
}
